import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { saveUseCase } from '../../api/onboarding';
import { updateLearner } from '../../api/learners';
import { useLearnerStore } from '../../stores/learnerStore';
import { useLearner } from '../../hooks/useLearner';
import { useOnboardingGuard } from '../../hooks/useOnboardingGuard';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import CloudBand from '../../components/common/CloudBand';
import WanderWordsLogo from '../../components/common/WanderWordsLogo';
import OnboardingCard from '../../components/common/OnboardingCard';
import OnboardingPageCard from '../../components/common/OnboardingPageCard';
import ToolTipLing from '../../components/common/ToolTipLing';
import s from './OnboardingPages.module.css';

const USE_CASES = [
  { key: 'standalone',       titleKey: 'useCase.atHome',   descKey: 'useCase.atHomeDesc' },
  { key: 'class_supplement', titleKey: 'useCase.withSchool', descKey: 'useCase.withSchoolDesc' },
  { key: 'heritage_speaker', titleKey: 'useCase.heritage',  descKey: 'useCase.heritageDesc' },
];

const STEP_COUNT = 5;

export default function UseCasePage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const location = useLocation();
  const { learner: learnerFromState } = location.state || {};
  const queryClient = useQueryClient();
  const setActiveLearnerId = useLearnerStore((s) => s.setActiveLearnerId);
  const { data: activeLearner } = useLearner();
  const learner = learnerFromState || activeLearner;

  useOnboardingGuard(3);

  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!learner?.id) {
      navigate('/onboarding/child-profile', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const learnerName = learner?.name || '';
  const currentStep = 3;

  async function handleContinue() {
    if (!selected || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      if (learner?.id) {
        await saveUseCase(learner.id, selected);
        const updated = await updateLearner(learner.id, { onboarding_step: 3 });
        // FP-LEARNER-STORE-RETIREMENT-01: optimistic patch + invalidate so
        // the confirm step reads the bumped onboarding_step from the
        // ['learners'] query rather than from a Zustand shadow.
        queryClient.setQueryData(['learners'], (prev) =>
          Array.isArray(prev)
            ? prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l))
            : prev
        );
        setActiveLearnerId(updated.id);
        queryClient.invalidateQueries({ queryKey: ['learners'] });
      }
      navigate('/onboarding/confirm', { state: { learner, useCase: selected } });
    } catch (err) {
      setError(err.message || t('common.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  }

  // Shared step counter markup
  const stepCounter = (
    <div className={s.stepCounter}>
      {Array.from({ length: STEP_COUNT }, (_, i) => {
        const step = i + 1;
        const isActive = step <= currentStep;
        return (
          <div
            key={step}
            className={`${s.stepDot} ${isActive ? s.stepDotActive : ''}`}
            aria-label={`Step ${step} of ${STEP_COUNT}`}
          >
            {step}
          </div>
        );
      })}
    </div>
  );

  // Shared option cards markup
  const optionCards = (
    <div className={s.optionCards} role="radiogroup" aria-label={t('useCase.title', { name: learnerName })}>
      {USE_CASES.map((uc) => (
        <OnboardingCard
          key={uc.key}
          title={t(uc.titleKey)}
          description={t(uc.descKey)}
          selected={selected === uc.key}
          onClick={() => setSelected(uc.key)}
        />
      ))}
    </div>
  );

  return (
    <main
      className={s.page}
      style={{ '--cloud-color': '#2AB8A8' }}
      ref={mainRef}
      tabIndex={-1}
    >
      {/* Desktop decoration — cream top band + cloud seam (hidden on mobile by CSS) */}
      <div className={`${s.topBand} ${s.topBandTeal}`} />
      <CloudBand variant="teal" className={s.cloudBandTeal} />
      <WanderWordsLogo className={s.logo} />

      {/* ───────────── MOBILE / TABLET LAYOUT (hidden ≥1200px) ───────────── */}
      <div className={s.mobileOnly}>
        <div className={s.content}>
          <div className={s.contentRow}>
            {/* Left column — teal section */}
            <div className={`${s.leftCol} ${s.leftColTeal}`}>
              {stepCounter}
              <div className={`${s.headerRow} ${s.headerRowTypeB}`}>
                <h1 className={s.pageTitle}>
                  {t('useCase.title', { name: learnerName })}
                </h1>
                <div className={s.subtitleLingRow}>
                  <p className={s.subtitle}>{t('useCase.subtitle')}</p>
                  <img
                    src="/assets/ling-onboarding.png"
                    alt="Ling the cat"
                    className={s.mascotImg}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Cream bottom section — cards + continue button */}
          <div className={s.mobileCreamSection}>
            <div className={s.rightCol}>
              {error && (
                <div className={s.errorBanner} role="alert">{error}</div>
              )}
              {optionCards}
              <div className={s.spacer} />
            </div>

            <div className={s.stickyBtnArea}>
              <div className={s.btnRow}>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={s.cancelBtn}
                >
                  {t('common.back')}
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!selected || isLoading}
                  className={s.continueBtn}
                >
                  {isLoading ? t('common.saving') : t('common.continue')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────── DESKTOP CARD (≥1200px only) ───────────── */}
      <OnboardingPageCard
        stepper={stepCounter}
        leftWidth={392}
        leftContent={
          <>
            <div className={s.headerColumn}>
              <h1 className={s.pageTitle}>
                {t('useCase.title', { name: learnerName })}
              </h1>
              <p className={s.subtitle}>{t('useCase.subtitle')}</p>
            </div>
            <ToolTipLing type="curious">
              {t('useCase.tooltipText')}
            </ToolTipLing>
          </>
        }
        rightContent={
          <>
            {error && (
              <div className={s.errorBanner} role="alert">{error}</div>
            )}
            {optionCards}
            <div className={s.btnRow}>
              <button type="button" onClick={() => navigate(-1)} className={s.cancelBtn}>
                {t('common.back')}
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!selected || isLoading}
                className={s.continueBtn}
              >
                {isLoading ? t('common.saving') : t('common.continue')}
              </button>
            </div>
          </>
        }
      />
    </main>
  );
}
